<div class="vault-page-shell">
  <div class="space-y-6">
    <a routerLink="/dashboard/accounts" class="vault-back-link">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M15 18l-6-6 6-6"/>
      </svg>
      Back to connected accounts
    </a>

    <div class="vault-page-header">
      <div class="space-y-3">
        <p class="vault-page-kicker">Account details</p>
        <h1 class="vault-page-title">{{ accountDisplayName }}</h1>
      </div>
      <div class="flex items-center gap-3">
        <span class="vault-status-pill is-active">Connected</span>
        <span class="vault-status-pill" [ngClass]="account?.platform_type === 'tefca' ? 'is-active' : 'is-warning'">
          {{ account?.platform_type || 'Unknown' }}
        </span>
      </div>
    </div>
  </div>

  <div class="vault-divider"></div>

  <div class="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
    <section class="vault-panel p-6 sm:p-8">
      <div class="flex flex-col gap-6">
        <div class="flex items-center gap-5">
          <img
            imageFallback
            [src]="'https://cdn.fastenhealth.com/logos/sources/' + account?.brand?.id + '.png'"
            [alt]="accountDisplayName"
            class="h-20 w-20 rounded-2xl border border-slate-200 bg-white object-contain p-3">

          <div class="space-y-2">
            <div>
              <p class="text-sm font-medium uppercase tracking-[0.24em] text-slate-400">Institution metadata</p>
              <h2 class="mt-2 text-2xl font-semibold text-slate-950">{{ account?.brand?.name || accountDisplayName }}</h2>
            </div>
          </div>
        </div>

        <div class="account-details-grid">
          <div class="account-details-item">
            <dt>Website</dt>
            <dd>
              <a [href]="institutionWebsite" target="_blank" rel="noreferrer" class="text-[#5B47FB] hover:text-[#4338ca]">
                {{ institutionWebsite }}
              </a>
            </dd>
          </div>
          <div class="account-details-item">
            <dt>Consent expires at</dt>
            <dd>{{ consentExpiresLabel }}</dd>
          </div>
          <div class="account-details-item">
            <dt>Vault connection ID</dt>
            <dd class="break-all">{{ account?.vault_profile_connection_id || 'Not available' }}</dd>
          </div>
          <div class="account-details-item">
            <dt>TEFCA Directory ID</dt>
            <dd class="break-all">{{ account?.tefca_directory_id || 'Not available' }}</dd>
          </div>
          <div class="account-details-item account-details-item--full">
            <dt>Authorization type</dt>
            <dd>{{ account?.patient_auth_type || 'Unknown' }}</dd>
          </div>
        </div>
      </div>
    </section>

    <section class="vault-panel p-6 sm:p-8">
      <div class="space-y-3">
        <p class="vault-page-kicker">Export actions</p>
        <h2 class="vault-card-title text-xl">Share or package this connection</h2>
        <p class="vault-card-copy">
          Use these options to share your health records from this connected account in the format that works best for you.
        </p>
      </div>

      <div class="mt-6 space-y-3">
        <button
          *ngFor="let action of exportActions"
          type="button"
          class="vault-secondary-button w-full justify-between px-4 py-4 text-left"
          (click)="openExportModal(action.type)">
          <span class="flex flex-col items-start gap-1">
            <span class="font-semibold text-slate-900">{{ action.label }}</span>
            <span class="text-sm font-normal text-slate-500">{{ action.description }}</span>
          </span>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M5 12h14"/>
            <path d="m12 5 7 7-7 7"/>
          </svg>
        </button>
      </div>
    </section>
  </div>

  <section *ngIf="configService.systemConfig$.apiMode === ApiMode.Test" class="vault-panel mt-6 p-6 sm:p-8">
    <div class="space-y-3">
      <p class="vault-page-kicker">Matched Demographics</p>
      <h2 class="vault-card-title text-xl">Patient Profile</h2>
      <p class="vault-card-copy">
        These demographics reflect the TEFCA-mode identity verification details used to match you to this institution.
      </p>
    </div>

    <dl class="account-details-grid mt-6">
      <div class="account-details-item">
        <dt>Name</dt>
        <dd>{{ formattedPatientName }}</dd>
      </div>
      <div class="account-details-item">
        <dt>Birthdate</dt>
        <dd>{{ formattedPatientBirthdate }}</dd>
      </div>
      <div class="account-details-item">
        <dt>Gender</dt>
        <dd class="capitalize">{{ patientDemographics?.gender || 'Not available' }}</dd>
      </div>
      <div class="account-details-item">
        <dt>Address</dt>
        <dd class="whitespace-pre-line">{{ formattedPatientAddress }}</dd>
      </div>
    </dl>
  </section>

  <div *ngIf="(configService.systemConfigSubject | async)?.apiMode == 'test'" class="mt-6 grid gap-6 xl:grid-cols-2">
    <section class="vault-panel p-3 sm:p-4">
      <div class="px-3 pt-3 sm:px-4 sm:pt-4">
        <p class="vault-page-kicker">Connected apps</p>
        <h2 class="vault-card-title text-xl">Apps using this connection</h2>
        <p class="vault-card-copy mt-2">Apps below are mocked examples of clients currently reading from this institution connection.</p>
      </div>

      <div class="mt-3 divide-y divide-slate-100">
        <div *ngFor="let app of appsUsingConnection" class="vault-list-row items-start">
          <div class="flex items-start gap-4">
            <div class="vault-icon-tile" [ngClass]="app.accentClasses">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20 14.66V20a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34"/>
                <polygon points="18 2 22 6 12 16 8 16 8 12 18 2"/>
              </svg>
            </div>
            <div class="space-y-2">
              <p class="font-semibold text-slate-900">{{ app.name }}</p>
              <div class="flex flex-wrap gap-x-3 gap-y-2 text-xs">
                <a
                  [href]="app.websiteUrl"
                  target="_blank"
                  rel="noreferrer"
                  class="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M2 12h20"/>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                  </svg>
                  <span>Website</span>
                </a>
                <a
                  [href]="app.privacyPolicyUrl"
                  target="_blank"
                  rel="noreferrer"
                  class="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                  <span>Privacy</span>
                </a>
                <a
                  [href]="app.termsOfUseUrl"
                  target="_blank"
                  rel="noreferrer"
                  class="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <path d="M14 2v6h6"/>
                    <path d="M8 13h8"/>
                    <path d="M8 17h5"/>
                  </svg>
                  <span>Terms</span>
                </a>
              </div>
            </div>
          </div>
          <button
            type="button"
            class="vault-secondary-button self-center border-rose-200 px-3 py-2 text-xs text-rose-600 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700"
            [attr.aria-label]="'Disconnect ' + app.name"
            [attr.title]="'Disconnect ' + app.name"
            (click)="disconnectMockApp(app.name)">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M3 6h18"/>
              <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2"/>
              <path d="M19 6l-1 14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1L5 6"/>
              <path d="M10 11v6"/>
              <path d="M14 11v6"/>
            </svg>
            <span>Remove</span>
          </button>
        </div>
      </div>

      <div *ngIf="appActionFeedback" class="mx-3 mb-3 mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700 sm:mx-4">
        {{ appActionFeedback }}
      </div>
    </section>

    <section class="vault-panel p-3 sm:p-4">
      <div class="px-3 pt-3 sm:px-4 sm:pt-4">
        <p class="vault-page-kicker">Activity history</p>
        <h2 class="vault-card-title text-xl">Recent access on this connection</h2>
        <p class="vault-card-copy mt-2">Recent app requests are mocked so we can validate the page structure before service integrations exist.</p>
      </div>

      <div class="mt-3 divide-y divide-slate-100">
        <div *ngFor="let activity of activityHistory" class="vault-list-row items-start">
          <div class="space-y-1">
            <div class="flex flex-wrap items-center gap-2">
              <p class="font-semibold text-slate-900">{{ activity.appName }}</p>
              <span class="vault-status-pill activity-status-pill is-warning">{{ activity.status }}</span>
            </div>
            <p class="text-sm text-slate-500">{{ activity.summary }}</p>
          </div>
          <p class="text-sm font-medium text-slate-400">{{ activity.timestamp }}</p>
        </div>
      </div>
    </section>
  </div>
</div>
<app-account-export-modal
  [isOpen]="isExportModalOpen"
  [exportType]="selectedExportType"
  [accountDisplayName]="accountDisplayName"
  [orgConnectionId]="account?.org_connection_id!"
  (closed)="closeExportModal()">
</app-account-export-modal>
